// Pure, form-library-agnostic input formatters: digit masking (SSN, date),
// currency cents↔display, and strict date parsing. No React deps, so the DS
// inputs and the app's form adapters share the same primitives.

import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

const DATE_MASK = "MM/DD/YYYY";

export const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;

/**
 * Masks digits into fixed-size groups joined by `separator`, dropping non-digits
 * as the user types. Digits past the last group are discarded, so the group sizes
 * also cap the length. e.g. `maskDigits("123456789", [3, 2, 4], "-")` → `"123-45-6789"`.
 */
export const maskDigits = (
  text: string,
  groups: number[],
  separator: string,
): string => {
  const digits = text.replace(/\D/g, "");
  const parts: string[] = [];
  let offset = 0;
  for (const size of groups) {
    if (offset >= digits.length) {
      break;
    }
    parts.push(digits.slice(offset, offset + size));
    offset += size;
  }
  return parts.join(separator);
};

/** Formats a canonical ISO `YYYY-MM-DD` date as display `MM/DD/YYYY`. Anything
 *  that isn't a complete ISO date (empty, or partial in-progress input) passes
 *  through unchanged, so a masked-input adapter can seed its display from stored
 *  ISO without mangling a non-ISO value. */
export const formatIsoDate = (value: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? `${match[2]}/${match[3]}/${match[1]}` : value;
};

/** Formats a partial date string to add slashes as the user types (MM/DD/YYYY).
 *  A complete ISO date (paste/autofill) is reordered to the mask first — masking
 *  it digit-blind would misread YYYY-MM-DD as MM/DD/YYYY and scramble the date. */
export const formatDate = (text: string): string =>
  maskDigits(formatIsoDate(text), [2, 2, 4], "/");

/** Masks a partial SSN as `XXX-XX-XXXX` (9 digits) as the user types. */
export const formatSsn = (text: string): string =>
  maskDigits(text, [3, 2, 4], "-");

/** Strict-parses masked `MM/DD/YYYY` text to an ISO `YYYY-MM-DD`, or `undefined`
 *  when it's incomplete or a calendar-invalid date (e.g. `15/12/2026`). */
export const dateTextToIso = (text: string): string | undefined => {
  if (!dateRegex.test(text)) {
    return undefined;
  }
  const parsed = dayjs(text, DATE_MASK, true);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : undefined;
};

type FormatCentsOptions = {
  currencySymbol?: string;
  precision?: number;
};

/**
 * Formats integer cents as a grouped currency string, e.g. `123456` → `"$1,234.56"`.
 * Integer cents avoids float drift; the component owns cents↔display conversion.
 * `undefined` → `""` so an empty field renders blank.
 */
export const formatCents = (
  cents: number | undefined,
  { currencySymbol = "$", precision = 2 }: FormatCentsOptions = {},
): string => {
  if (cents === undefined) {
    return "";
  }
  const amount = Math.abs(cents) / 10 ** precision;
  const grouped = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  }).format(amount);
  const sign = cents < 0 ? "-" : "";
  return `${sign}${currencySymbol}${grouped}`;
};

type ParseCentsOptions = {
  precision?: number;
};

/**
 * Parses a display string back to integer minor units, e.g. `"$1,234.56"` →
 * `123456`. Strips the currency symbol and grouping separators. `precision`
 * selects the minor-unit scale (default 2). Empty/invalid → `undefined`.
 */
export const parseCents = (
  display: string,
  { precision = 2 }: ParseCentsOptions = {},
): number | undefined => {
  const cleaned = display.replace(/[^\d.-]/g, "");
  if (cleaned === "" || cleaned === "-" || cleaned === ".") {
    return undefined;
  }
  const amount = Number.parseFloat(cleaned);
  if (Number.isNaN(amount)) {
    return undefined;
  }
  return Math.round(amount * 10 ** precision);
};
